import { useEffect, useState } from "react";
import { Trophy, Check, Medal } from "lucide-react";
import { toast } from "sonner";
import { api, useContent } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Challenges() {
  const { user, refresh } = useAuth();
  const { data: challenges } = useContent("challenges");
  const [board, setBoard] = useState([]);
  const joined = new Set(user?.challenges || []);

  useEffect(() => { api.get("/me/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); }, []);

  const join = async (id, title) => {
    try { await api.post(`/me/challenges/${id}/join`); await refresh(); toast.success(`Joined ${title}!`); }
    catch { toast.error("Could not join."); }
  };

  return (
    <div data-testid="challenges-page" className="px-4 md:px-8 pb-16 pt-2">
      <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Track &amp; Win</h1>
      <p className="text-muted-foreground mt-1">Earn points for staying active, join challenges and climb the leaderboard.</p>

      {/* Rewards / Level panel */}
      <div className="mt-6 border border-border rounded-xl bg-card p-6" data-testid="rewards-panel">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Your Points</div>
            <div className="font-display font-900 text-6xl text-primary leading-none">{user?.points || 0}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Level</div>
            <div className="font-display font-900 text-6xl leading-none">{Math.floor((user?.points || 0) / 100) + 1}</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Level {Math.floor((user?.points || 0) / 100) + 1}</span><span>{(user?.points || 0) % 100}/100 to next</span></div>
          <div className="h-2.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(user?.points || 0) % 100}%` }} /></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5 text-xs">
          {[["Workout", "+10"], ["Daily login", "+5"], ["Join challenge", "+15"], ["Read blog", "+3"], ["Watch video", "+3"], ["Log a meal", "+5"], ["Water goal", "+5"], ["Community post", "+5"]].map(([l, p]) => (
            <div key={l} className="flex items-center justify-between border border-border rounded-lg px-3 py-2"><span className="text-muted-foreground">{l}</span><span className="font-bold text-primary">{p}</span></div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 grid gap-5 sm:grid-cols-2">
          {challenges.map((c) => {
            const isJoined = joined.has(c.id);
            return (
              <div key={c.id} data-testid="challenge-card" className="border border-border rounded-xl overflow-hidden bg-card">
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
                  <span className="absolute top-3 left-3 text-[10px] uppercase font-bold bg-black/70 text-white px-2 py-1 rounded">{c.days} Days</span>
                </div>
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-wide text-primary font-bold">{c.difficulty}</span>
                  <h3 className="font-display font-700 uppercase text-2xl tracking-tight mt-1">{c.title}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{c.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3"><Trophy size={13} className="text-primary" /> {c.reward}</div>
                  <button onClick={() => join(c.id, c.title)} disabled={isJoined} data-testid="challenge-join"
                    className={`mt-4 w-full py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold transition-transform ${isJoined ? "bg-secondary text-muted-foreground" : "bg-primary text-primary-foreground hover:scale-[1.02]"}`}>
                    {isJoined ? <span className="inline-flex items-center gap-1.5"><Check size={14} /> Joined</span> : "Join Challenge"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border border-border rounded-xl bg-card p-6 h-fit" data-testid="leaderboard">
          <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4 flex items-center gap-2"><Medal size={20} className="text-primary" /> Leaderboard</h3>
          <div className="space-y-3">
            {board.length === 0 && <p className="text-sm text-muted-foreground">No rankings yet — be the first!</p>}
            {board.map((u, i) => (
              <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${u.name === user?.name ? "bg-primary/5" : ""}`}>
                <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${i < 3 ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0 font-semibold text-sm truncate">{u.name || "Member"}</div>
                <div className="text-sm font-bold text-primary">{u.points} pts</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
